import { api } from '@smena/ui';

// Канал «Telegram - личный аккаунт УК» через self-hosted шлюз tg-gateway (ADR-20).
// Не путать с общим ботом Smena в Telegram (features/channels): там Bot API и наш бот,
// здесь личный аккаунт УК, привязанный по QR как ещё одно устройство.
//
// Зеркало контракта app/apps/api/src/modules/tg-personal/tg-personal.controller.ts,
// типы повторяют TgPersonalSessionView / TgDialogView из @smena/channels. Дублируем
// намеренно: панель не тянет серверные пакеты, у неё своя граница сборки.

export type TgPersonalStatus =
  | 'qr_pending'
  | 'password_required'
  | 'authorized'
  | 'degraded'
  | 'flood_wait'
  | 'disconnected'
  | 'banned';

/** Ось прогрева номера: чем «холоднее» сессия, тем жёстче лимиты на отправку. */
export type TgWarmup = 'cold' | 'warming' | 'warm';

export interface TgPersonalSession {
  sessionId: string;
  accountId: string;
  status: TgPersonalStatus;
  /** Нормализованные цифры без плюса, приходит после входа из getMe(). */
  phone?: string;
  tgUserId?: number;
  warmupState: TgWarmup;
  hasProxy: boolean;
  /** Уже отредактированный вид `user:***@host:port`. Полный URL наружу не отдаётся. */
  proxyLabel?: string;
  lastError?: string;
  /** ISO: до этого момента Telegram держит паузу на отправку. */
  floodUntil?: string;
  qr?: {
    link: string;
    /** dataURL PNG - вставляем прямо в <img src>. Ссылка ротируется раз в ~30 секунд. */
    image: string;
    expiresAtMs: number;
  };
}

export interface TgDialog {
  peerId: number;
  title: string;
  phone: string | null;
  username: string | null;
}

const BASE = '/api/staff/channels/tg-personal';

export function fetchTgSessions(): Promise<{ items: TgPersonalSession[]; available: boolean }> {
  return api(BASE);
}

export function fetchTgSession(sessionId: string): Promise<TgPersonalSession> {
  return api(`${BASE}/${sessionId}`);
}

export function connectTgSession(input: {
  phone?: string;
}): Promise<TgPersonalSession> {
  return api(BASE, { method: 'POST', json: input });
}

export function submitTgPassword(sessionId: string, password: string): Promise<TgPersonalSession> {
  return api(`${BASE}/${sessionId}/password`, { method: 'POST', json: { password } });
}

/** Меняем SOCKS5 без релогина: шлюз проверяет новый адрес пробой до сохранения. */
export function setTgProxy(sessionId: string, proxyUrl: string): Promise<TgPersonalSession> {
  return api(`${BASE}/${sessionId}/proxy`, { method: 'POST', json: { proxyUrl } });
}

/** Есть ли номер гостя в Telegram - обязательная проверка перед первым касанием. */
export function resolveTgPhone(sessionId: string, phone: string): Promise<{ exists: boolean }> {
  return api(`${BASE}/${sessionId}/resolve-phone`, { method: 'POST', json: { phone } });
}

/** Диалоги аккаунта - выбор переписок для базы знаний (экран выгрузки, фаза Ф6). */
export function fetchTgDialogs(sessionId: string, limit = 100): Promise<{ items: TgDialog[] }> {
  return api(`${BASE}/${sessionId}/dialogs?limit=${limit}`);
}

export function disconnectTgSession(sessionId: string): Promise<{ ok: true }> {
  return api(`${BASE}/${sessionId}`, { method: 'DELETE' });
}

export interface TgHistoryImportResult {
  imported: number;
  skipped: number;
  messages: number;
  errors: string[];
}

/** Забрать переписки выбранных диалогов личного аккаунта в базу знаний. */
export function importTgHistory(
  sessionId: string,
  peerIds: number[],
  limit = 300,
): Promise<TgHistoryImportResult> {
  return api(`/api/staff/channels/tg-personal/${sessionId}/import-history`, {
    method: 'POST',
    json: { peerIds, limit },
  });
}

/** Правило игнора: чат, в который агент не пишет и который не парсит. */
export interface IgnoredChatRule {
  id: string;
  ref: string;
  kind: string;
  note: string | null;
  vendor: boolean;
}

export function fetchTgIgnored(sessionId: string): Promise<{ items: IgnoredChatRule[] }> {
  return api(`/api/staff/channels/tg-personal/${sessionId}/ignored`);
}

export function addTgIgnored(
  sessionId: string,
  chatId: string,
  note?: string,
): Promise<IgnoredChatRule> {
  return api(`/api/staff/channels/tg-personal/${sessionId}/ignored`, {
    method: 'POST',
    json: { chatId, note },
  });
}

export function removeTgIgnored(sessionId: string, ruleId: string): Promise<{ ok: true }> {
  return api(`/api/staff/channels/tg-personal/${sessionId}/ignored/${ruleId}`, { method: 'DELETE' });
}
